
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import SettingsPanel from '@components/settings/SettingsPanel.vue';
import AppListItem, { ListItem } from '@components/new-styleguide/AppListItem.vue';
import storageService from '@util/storageService';
import AppPage from '@components/new-styleguide/AppPage.vue';
import AppHeader from '@components/new-styleguide/AppHeader.vue';
import AppContent from '@components/new-styleguide/AppContent.vue';
import { LoadState } from '@/utils/types';
import { TripLog } from '@/generated-clients/myTrips';
import { deepEquals } from '@/utils/copy';

const TriplogModule = namespace('Triplog');
const SettingsModule = namespace('Settings');

@Component({
  name: 'mySettingsRework',
  components: {
    AppContent,
    SettingsPanel,
    AppListItem,
    AppHeader,
    AppPage
  },
})
export default class SettingsReworkScreen extends Vue {
  @TriplogModule.Getter('getVehiclesStatus')
  private vehiclesStatus;

  @TriplogModule.Action('fetchVehicles')
  private fetchVehicles;

  @TriplogModule.Getter('getVehicles')
  private tripLogs: TripLog[];

  @SettingsModule.Action('fetchPilots')
  private fetchPilots;

  @SettingsModule.Action('fetchPilotSuggestions')
  private fetchPilotSuggestions;

  @SettingsModule.Action('fetchTags')
  private fetchTags;

  @SettingsModule.Action('fetchZones')
  private fetchZones;

  @SettingsModule.Action('fetchRulesV2')
  private fetchRules;

  @SettingsModule.Action('fetchZoneSuggestions')
  private fetchZoneSuggestions;

  @SettingsModule.Getter('getTagsStatus')
  private tagsStatus: LoadState;

  @SettingsModule.Getter('getPilotsStatus')
  private pilotsStatus: LoadState;

  @SettingsModule.Getter('getZonesStatus')
  private zonesStatus: LoadState;

  @SettingsModule.Getter('getRulesV2Status')
  private rulesStatus: LoadState

  private vehicleId: number = null;

  async created() {
    if (this.vehiclesStatus !== LoadState.Success) {
      const tripLogs: TripLog[] = await this.fetchVehicles();
      if (tripLogs.length === 0) {
        const path = this.$route.path.replace(/\/$/, '').split('/').slice(0, -1).join('/') || '/';
        this.$router.push({ path });
      }
    }

    if (this.$route.query.vehicle && this.tripLogs.some(vehicle => vehicle.vehicle.id.toString() === this.$route.query.vehicle)) {
      this.vehicleId = parseInt(this.$route.query.vehicle as string);
    } else if (this.tripLogs.length === 1) {
      this.vehicleId = this.tripLogs[0].vehicle.id;
    } else if (storageService.getVehicleId() && this.tripLogs.some(vehicle => vehicle.vehicle.id.toString() === storageService.getVehicleId())) {
      this.vehicleId = parseInt(storageService.getVehicleId());
    }

    this.update();
  }

  private update() {
    this.updateQueryParams();
    this.updateLocalStorage();
    this.updateSettings();
  }

  private updateQueryParams() {
    if (this.vehicleId) {
      const query = {
        vehicle: this.vehicleId.toString()
      };

      if (!deepEquals(query, this.$route.query)) {
        this.$router.replace({ query });
      }
    }
  }

  private updateLocalStorage() {
    if (this.vehicleId) {
      storageService.setVehicleId(this.vehicleId);
    } else {
      storageService.removeVehicleId();
    }
  }

  private updateSettings() {
    this.fetchPilots();
    this.fetchPilotSuggestions();
    this.fetchTags();
    this.fetchZones();
    this.fetchRules();
    this.fetchZoneSuggestions();
  }

  get settings(): ListItem[] {
    return [
      { title: 'Personen', location: { name: 'pilotsOverview' }, tagIcon: this.settingIcon('tag_people') },
      { title: 'Kategorien', location: { name: 'tagsOverview' }, tagIcon: this.settingIcon('tag_category') },
      { title: 'Häufige Ziele', location: { name: 'zonesOverview' }, tagIcon: this.settingIcon('tag_marker') },
      { title: 'Regeln', location: { name: 'rulesOverview' }, tagIcon: this.settingIcon('tag_rule') },
      { title: 'Erinnerungen', location: { name: 'maintenanceOverview' }, tagIcon: this.settingIcon('tag_notification', 10) },
      { title: 'Informationen', location: { name: 'informationOverview' }, tagIcon: this.settingIcon('tag_list', 10) }
    ]
  }

  private settingIcon(value: string, position?: number) {
    return { value, fill: 'black', color: '#FFDC00', position };
  }

  get headerText() {
    return "Automatisieren Sie Ihr Fahrtenbuch mit häufigen Zielen und Regeln. Sorgen Sie mit Personen und Kategorien für Farbe und Übersicht. Anpassungen betreffen all Ihre Fahrzeuge.";
  }

  get selectedVehicle() {
    return this.vehicleId && this.tripLogs.find(tripLog => tripLog.vehicle.id === this.vehicleId);
  }

  get loadingStatuses() {
    return [this.vehiclesStatus, this.tagsStatus, this.pilotsStatus, this.zonesStatus, this.rulesStatus];
  }

  get isLoading() {
    return this.loadingStatuses.some(status => status === LoadState.Loading || status === LoadState.None);
  }

  get errorLoading() {
    return this.loadingStatuses.some(status => status === LoadState.Error);
  }
}
